import { Controller } from "@hotwired/stimulus"

// THE shell controller (mobile register, 2026-09-02): one drawer, one menu
// button, one back rule, for all five staff layouts. It owns exactly one
// piece of state, `data-nav-open` on <body>; uds-shell.css draws everything
// from that attribute. Replaces sidebar, prt-sidebar and spt-sidebar.
//
// The query string below is the desktop tier and must stay byte-equal to
// the one in uds-shell.css and application.tailwind.css (static scan).
const DESKTOP = "(min-width: 1024px) and (min-height: 500px)"

export default class extends Controller {
  static targets = ["drawer", "menu", "main"]

  connect() {
    this.tier = window.matchMedia(DESKTOP)
    this.onTier = () => this.sync()
    this.onKey = (event) => { if (event.key === "Escape" && this.isOpen) this.close() }
    // A cached snapshot must never come back with the drawer open.
    this.onCache = () => this.close(false)
    this.tier.addEventListener("change", this.onTier)
    document.addEventListener("keydown", this.onKey)
    document.addEventListener("turbo:before-cache", this.onCache)
    this.sync()
  }

  disconnect() {
    this.tier.removeEventListener("change", this.onTier)
    document.removeEventListener("keydown", this.onKey)
    document.removeEventListener("turbo:before-cache", this.onCache)
  }

  get isOpen() { return this.element.hasAttribute("data-nav-open") }

  toggle() { this.isOpen ? this.close() : this.open() }

  open() {
    if (this.tier.matches) return
    this.element.setAttribute("data-nav-open", "")
    this.sync()
    // Focus lands immediately: transitionend never fires under reduced motion.
    this.drawerTarget.querySelector("a, button")?.focus()
  }

  // The overlay's click action lands here (an event is not a refocus flag).
  dismiss() { this.close() }

  close(refocus = true) {
    if (!this.isOpen) return
    this.element.removeAttribute("data-nav-open")
    // Order matters: the main column comes back first, then focus lands on
    // the menu button, then the drawer goes inert (sync). Focus set on an
    // inert element is silently dropped.
    if (this.hasMainTarget) this.mainTarget.inert = false
    if (refocus && this.hasMenuTarget) this.menuTarget.focus()
    this.sync()
  }

  // One truth table: on desktop the drawer is a plain sidebar; on phones it
  // is a dialog when open and inert when closed.
  sync() {
    if (!this.hasDrawerTarget) return // a layout branch with no drawer (signed out)
    const desktop = this.tier.matches
    const open = !desktop && this.isOpen
    if (desktop && this.isOpen) this.element.removeAttribute("data-nav-open")
    this.drawerTarget.inert = !desktop && !open
    // aria-modal is only true if the page behind the drawer is really inert.
    if (this.hasMainTarget) this.mainTarget.inert = open
    if (open) {
      this.drawerTarget.setAttribute("role", "dialog")
      this.drawerTarget.setAttribute("aria-modal", "true")
    } else {
      this.drawerTarget.removeAttribute("role")
      this.drawerTarget.removeAttribute("aria-modal")
    }
    if (this.hasMenuTarget) this.menuTarget.setAttribute("aria-expanded", String(open))
  }

  // The back chevron is an anchor with a real href (the server's best
  // guess). Turbo records a restoration index on every visit it made in
  // THIS document; a fresh document (cold launch, a tap on a push banner,
  // a turbo:false form redirect) starts at 0 and follows the href. That is
  // the whole rule: no referrer (frozen at document creation), no counter.
  back(event) {
    const index = history.state?.turbo?.restorationIndex
    if (index > 0) {
      event.preventDefault()
      history.back()
    }
  }
}
